import {
  useMemo,
  useRef,
  useLayoutEffect,
  useCallback,
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from 'react'

import { Spin } from 'antd'
import classNames from 'classnames'
import { useFormik } from 'formik'

import { Button } from 'common/components/Button/Button'
import { BUTTON_SEVERITY } from 'common/constants/buttonConstants'
import { SignatureDialog } from 'common/components/SignatureDialog'
import { useNotification } from 'app/providers'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import {
  useFetchDocTemplateQuery,
  useSubmitDocTemplateMutation,
} from 'features/Procedure/state/procedure.api'

import {
  flattenValues,
  extractFormFields,
  findElementByFieldKey,
  getRequiredFieldNames,
  getRequiredFieldValues,
  setNestedValue,
  getTemplateValidationError,
} from './utils'
import { buildBaseHtml, mapSubmitDocTemplateErrorMessage } from './dialog-helpers'
import styles from './doc-template-preview-dialog.module.scss'

type DocTemplateFormProps = {
  patientId: number
  encounterUid: string
  templateId: number
  title?: string
  onSubmitSuccess: (templateId: number) => void
  onDirtyChange?: (dirty: boolean) => void
  onBusyChange?: (busy: boolean) => void
  onReadyChange?: (ready: boolean) => void
  onCancel?: () => void
  embedded?: boolean
}

export type DocTemplateFormHandle = {
  submit: () => void
}

type SignatureCanvasSize = {
  width: number
  height: number
}

const DEFAULT_SIGNATURE_CANVAS_SIZE: SignatureCanvasSize = { width: 440, height: 200 }

// The OpenEMR templates render their text fields quite small, which is awkward
// to fill in with a stylus on a tablet. Enlarge the editable fields so they are
// easier to write in: double the height and add 50% width. Checkboxes, radios,
// buttons and signature images keep their original size.
const FIELD_HEIGHT_MULTIPLIER = 2
const FIELD_WIDTH_MULTIPLIER = 1.5
const EDITABLE_FIELD_SELECTOR =
  'input:not([type="checkbox"]):not([type="radio"]):not([type="hidden"]):not([type="image"]):not([type="button"]):not([type="submit"]):not([type="reset"]), textarea'

// Measure-and-scale on the already-rendered nodes (rather than in the template
// string) so input identity stays stable and tablet handwriting keeps working.
// Templates size their inputs in different ways (inline width, `size`, defaults),
// so scaling the measured box is the only reliable way to grow each field
// relative to its current size.
const enlargeEditableFields = (container: HTMLElement): void => {
  container.querySelectorAll<HTMLElement>(EDITABLE_FIELD_SELECTOR).forEach((field) => {
    const { width, height } = field.getBoundingClientRect()
    if (width > 0) field.style.width = `${width * FIELD_WIDTH_MULTIPLIER}px`
    if (height > 0) field.style.height = `${height * FIELD_HEIGHT_MULTIPLIER}px`
  })
}

export const DocTemplateForm = forwardRef<DocTemplateFormHandle, DocTemplateFormProps>(
  (
    {
      patientId,
      encounterUid,
      templateId,
      title = 'Document Preview',
      onSubmitSuccess,
      onDirtyChange,
      onBusyChange,
      onReadyChange,
      onCancel,
      embedded = false,
    },
    ref,
  ) => {
    const {
      data: response,
      isLoading,
      isFetching,
    } = useFetchDocTemplateQuery(
      { patientId, encounterUid, templateId },
      { skip: !patientId || !encounterUid || !templateId },
    )

    const [submitDocTemplate, { isLoading: isSubmitting }] = useSubmitDocTemplateMutation()
    const { setNotification } = useNotification()

    const isLoadingContent = isLoading || isFetching
    const templateData = response?.data
    const templateContent = templateData?.template_content || ''
    const templateFieldData = templateData?.template_data
    const documentTitle =
      templateData?.file_name || (typeof title === 'string' ? title : 'Document Preview')
    const isTemplateMatched = Boolean(templateData) && templateData?.id === templateId

    const baseHtml = useMemo(() => {
      return buildBaseHtml(templateContent, templateFieldData)
    }, [templateContent, templateFieldData])

    const initialValues = useMemo(() => {
      if (!baseHtml) return {}
      return extractFormFields(baseHtml)
    }, [baseHtml])

    const {
      patientSignatureFieldName,
      physicianSignatureFieldName,
      witnessNameFieldName,
      witnessSignatureFieldName,
    } = useMemo(() => getRequiredFieldNames(baseHtml), [baseHtml])

    const formik = useFormik<Record<string, unknown>>({
      initialValues,
      enableReinitialize: true,
      onSubmit: async (values) => {
        const flat = flattenValues(values)
        const templateDataPayload = Object.entries(flat).map(([name, value]) => ({ name, value }))

        const validationError = getTemplateValidationError(
          values,
          {
            patientSignatureFieldName,
            physicianSignatureFieldName,
            witnessNameFieldName,
            witnessSignatureFieldName,
          },
          formik.initialValues,
        )
        if (validationError) {
          setNotification({
            type: ALERT_CONSTANTS.ERROR,
            duration: 2,
            title: 'Error',
            description: validationError,
          })
          return
        }

        const { patientSignature } = getRequiredFieldValues(values, {
          patientSignatureFieldName,
          physicianSignatureFieldName,
          witnessNameFieldName,
          witnessSignatureFieldName,
        })

        try {
          await submitDocTemplate({
            patientId,
            templateId,
            encounterUid,
            payload: {
              patientSignature,
              templateData: JSON.stringify(templateDataPayload),
            },
          }).unwrap()

          onSubmitSuccess(templateId)
        } catch (error: unknown) {
          const message =
            typeof error === 'object' && error !== null && 'data' in error
              ? (error as { data?: { message?: string } }).data?.message
              : undefined
          setNotification({
            type: ALERT_CONSTANTS.ERROR,
            duration: 2,
            title: 'Error',
            description: mapSubmitDocTemplateErrorMessage(message),
          })
        }
      },
    })

    useEffect(() => {
      onDirtyChange?.(formik.dirty)
    }, [formik.dirty, onDirtyChange])

    useEffect(() => {
      onBusyChange?.(isLoadingContent || isSubmitting)
    }, [isLoadingContent, isSubmitting, onBusyChange])

    useEffect(() => {
      onReadyChange?.(!isLoadingContent && isTemplateMatched)
    }, [isLoadingContent, isTemplateMatched, onReadyChange])

    useImperativeHandle(ref, () => ({ submit: () => formik.handleSubmit() }), [formik])

    const contentRef = useRef<HTMLDivElement>(null)

    const [signatureFieldName, setSignatureFieldName] = useState<string | null>(null)
    const [signatureCanvasSize, setSignatureCanvasSize] = useState<SignatureCanvasSize>(
      DEFAULT_SIGNATURE_CANVAS_SIZE,
    )

    const handleOpenSignatureDialog = useCallback((fieldName: string, canvasRatio?: number) => {
      if (canvasRatio && Number.isFinite(canvasRatio) && canvasRatio > 0) {
        const width = DEFAULT_SIGNATURE_CANVAS_SIZE.width
        const height = Math.max(90, Math.min(260, Math.round(width / canvasRatio)))
        setSignatureCanvasSize({ width, height })
      } else {
        setSignatureCanvasSize(DEFAULT_SIGNATURE_CANVAS_SIZE)
      }

      setSignatureFieldName(fieldName)
    }, [])

    const handleCloseSignatureDialog = useCallback(() => {
      setSignatureFieldName(null)
      setSignatureCanvasSize(DEFAULT_SIGNATURE_CANVAS_SIZE)
    }, [])

    const handleSignatureComplete = useCallback(
      (signatureDataUrl: string) => {
        if (!signatureFieldName) return

        formik.setValues((prevValues) =>
          setNestedValue(prevValues, signatureFieldName, signatureDataUrl),
        )

        // The signature value comes from React state, not a native input, so it
        // must be pushed into the already-rendered DOM. Update only this single
        // <img> rather than re-rendering the whole template, which would recreate
        // every input and break tablet handwriting.
        const container = contentRef.current
        const imageElement = container
          ? (findElementByFieldKey(container, signatureFieldName) as HTMLImageElement | null)
          : null
        if (imageElement instanceof HTMLImageElement) {
          imageElement.src = signatureDataUrl
        }
      },
      [signatureFieldName, formik.setValues],
    )

    useLayoutEffect(() => {
      if (isLoadingContent || !baseHtml) return

      const container = contentRef.current
      if (!container) return

      // Render the template once into a DOM subtree that React does not manage.
      // React re-applies `dangerouslySetInnerHTML` (recreating every input) on
      // each re-render, which detaches tablet handwriting from the field being
      // written in. Setting innerHTML imperatively keeps the input nodes stable
      // across form-value updates, so stylus/Scribble handwriting keeps working.
      container.innerHTML = baseHtml

      // Embedded intake matches the OpenEMR portal 1:1 (wume-forms.css); only
      // the preview dialog grows fields for tablet writing.
      if (!embedded) enlargeEditableFields(container)

      const getSignatureImageTarget = (target: HTMLElement): HTMLImageElement | null => {
        const imgTarget =
          target instanceof HTMLImageElement
            ? target
            : (target.closest('img') as HTMLImageElement | null)
        if (!(imgTarget instanceof HTMLImageElement)) return null

        const dataType = (imgTarget.getAttribute('data-type') || '').toLowerCase()
        const imageId = (imgTarget.getAttribute('id') || '').toLowerCase()
        const imageName = (imgTarget.getAttribute('name') || '').toLowerCase()
        const isSignatureImage =
          imgTarget.classList.contains('signature') ||
          dataType.includes('signature') ||
          imgTarget.getAttribute('data-action') === 'fetch_signature' ||
          imageId.includes('signature') ||
          imageName.includes('signature')

        return isSignatureImage ? imgTarget : null
      }

      const signatureImages = container.querySelectorAll<HTMLImageElement>('img')
      signatureImages.forEach((img) => {
        if (!getSignatureImageTarget(img)) return
        img.draggable = false
        img.setAttribute('draggable', 'false')
      })

      const handleClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement
        const imgTarget = getSignatureImageTarget(target)
        if (!imgTarget) return

        e.preventDefault()
        const imageName = imgTarget.getAttribute('name')
        const imageFieldKey = imageName || imgTarget.getAttribute('id')
        const imageId = (imgTarget.getAttribute('id') || '').toLowerCase()
        const hasAdminOrPhysicianSignature =
          (imageId.includes('admin') ||
            imageId.includes('physician') ||
            imageId.includes('doctor')) &&
          imageId.includes('signature')
        const name =
          imageFieldKey ||
          (imageId.includes('witness') && imageId.includes('signature')
            ? witnessSignatureFieldName
            : null) ||
          (hasAdminOrPhysicianSignature ? physicianSignatureFieldName : null) ||
          (imageId.includes('patient') && imageId.includes('signature')
            ? patientSignatureFieldName
            : null) ||
          (imgTarget.id === 'patientSignature' ? patientSignatureFieldName : null)

        if (name) {
          const rect = imgTarget.getBoundingClientRect()
          const ratio = rect.width > 0 && rect.height > 0 ? rect.width / rect.height : undefined
          handleOpenSignatureDialog(name, ratio)
        }
      }

      const handleDragStart = (e: DragEvent) => {
        const target = e.target as HTMLElement
        const imgTarget = getSignatureImageTarget(target)
        if (!imgTarget) return
        e.preventDefault()
      }

      const handleChange = (e: Event) => {
        const target = e.target as HTMLElement
        if (
          target instanceof HTMLInputElement ||
          target instanceof HTMLTextAreaElement ||
          target instanceof HTMLSelectElement
        ) {
          const fieldName = target.getAttribute('name') || target.getAttribute('id')
          if (!fieldName) return

          if (target instanceof HTMLInputElement && target.type === 'checkbox') {
            formik.setValues((prevValues) =>
              setNestedValue(prevValues, fieldName, target.checked ? 'on' : ''),
            )
          } else {
            formik.setValues((prevValues) => setNestedValue(prevValues, fieldName, target.value))
          }
        }
      }

      container.addEventListener('click', handleClick)
      container.addEventListener('dragstart', handleDragStart)
      container.addEventListener('change', handleChange)

      return () => {
        container.removeEventListener('click', handleClick)
        container.removeEventListener('dragstart', handleDragStart)
        container.removeEventListener('change', handleChange)
      }
    }, [
      isLoadingContent,
      baseHtml,
      embedded,
      handleOpenSignatureDialog,
      formik.setValues,
      patientSignatureFieldName,
      physicianSignatureFieldName,
      witnessSignatureFieldName,
    ])

    return (
      <>
        <div className={styles.container}>
          {!embedded && <h2 className={styles.dialogTitle}>{documentTitle}</h2>}
          <div
            className={classNames(styles.contentWrapper, {
              [styles.contentWrapperEmbedded]: embedded,
            })}>
            {isLoadingContent ? (
              <div className={styles.loadingWrapper}>
                <Spin size='large' />
              </div>
            ) : embedded ? (
              // OpenEMR portal shell wrappers that wume-forms.css is scoped to.
              <div id='templatediv' className={classNames(styles.openEmrContainer, 'card-body')}>
                <div ref={contentRef} id='templatecontent' className='template-body' />
              </div>
            ) : (
              <div ref={contentRef} className={styles.openEmrContainer} />
            )}
          </div>
          {!embedded && !isLoadingContent && (
            <div className={styles.actions}>
              {onCancel && (
                <Button severity={BUTTON_SEVERITY.DANGER} onClick={onCancel}>
                  Cancel
                </Button>
              )}
              <Button
                onClick={() => formik.handleSubmit()}
                loading={isSubmitting}
                disabled={isSubmitting || !isTemplateMatched}>
                Submit
              </Button>
            </div>
          )}
        </div>

        <SignatureDialog
          isVisible={Boolean(signatureFieldName)}
          onClose={handleCloseSignatureDialog}
          onSign={handleSignatureComplete}
          title='Draw Your Signature'
          canvasWidth={signatureCanvasSize.width}
          canvasHeight={signatureCanvasSize.height}
        />
      </>
    )
  },
)

DocTemplateForm.displayName = 'DocTemplateForm'
